/**
 * Shadow Tokens
 *
 * Elevation and depth system using dual-layer shadows for realistic depth.
 * Includes shadows for cards, navigation bars, modals, dropdowns and the pressed FAB.
 *
 * @see DS-006-shadows.md
 * @figma https://www.figma.com/design/NXGWL5ErhbsA0kqoybqQbg/Design-System?node-id=170-12483
 */

export interface ShadowLayer {
  color: string;
  offsetX: number;
  offsetY: number;
  blurRadius: number;
  spreadRadius: number;
}

export interface Shadow {
  layers: ShadowLayer[];
}

/** Shadow base color (grey-900) */
const SHADOW_BASE = "#202327";

/** Opacity variants (hex suffix) */
const OPACITY = {
  2: "05", // 2%
  4: "0A", // 4%
  6: "0F", // 6%
  8: "14", // 8%
  12: "1F", // 12%
} as const;

export const shadows = {
  /** Standard card shadow */
  card: {
    layers: [
      {
        color: `${SHADOW_BASE}${OPACITY[8]}`,
        offsetX: 0,
        offsetY: 4,
        blurRadius: 12,
        spreadRadius: -1,
      },
      {
        color: `${SHADOW_BASE}${OPACITY[12]}`,
        offsetX: 0,
        offsetY: 0,
        blurRadius: 4,
        spreadRadius: -4,
      },
    ],
  },
  /** Card hover/focus state */
  "card-hovered": {
    layers: [
      {
        color: `${SHADOW_BASE}${OPACITY[12]}`,
        offsetX: 0,
        offsetY: 12,
        blurRadius: 42,
        spreadRadius: -4,
      },
      {
        color: `${SHADOW_BASE}${OPACITY[12]}`,
        offsetX: 0,
        offsetY: 8,
        blurRadius: 18,
        spreadRadius: -6,
      },
    ],
  },
  /** Large/feature card shadow */
  bigcard: {
    layers: [
      {
        color: `${SHADOW_BASE}${OPACITY[8]}`,
        offsetX: 0,
        offsetY: 8,
        blurRadius: 32,
        spreadRadius: -6,
      },
      {
        color: `${SHADOW_BASE}${OPACITY[12]}`,
        offsetX: 0,
        offsetY: 6,
        blurRadius: 20,
        spreadRadius: -6,
      },
    ],
  },
  /** Large card hover state */
  "bigcard-hovered": {
    layers: [
      {
        color: `${SHADOW_BASE}${OPACITY[12]}`,
        offsetX: 0,
        offsetY: 12,
        blurRadius: 42,
        spreadRadius: -4,
      },
      {
        color: `${SHADOW_BASE}${OPACITY[12]}`,
        offsetX: 0,
        offsetY: 8,
        blurRadius: 32,
        spreadRadius: -6,
      },
    ],
  },
  /** Top navigation bar shadow */
  topbar: {
    layers: [
      {
        color: `${SHADOW_BASE}${OPACITY[4]}`,
        offsetX: 0,
        offsetY: 8,
        blurRadius: 16,
        spreadRadius: -6,
      },
      {
        color: `${SHADOW_BASE}${OPACITY[6]}`,
        offsetX: 0,
        offsetY: 6,
        blurRadius: 8,
        spreadRadius: -6,
      },
    ],
  },
  /**
   * Bottom navigation bar shadow. Mirrors `topbar` with the Y-offset
   * flipped so it projects upward from a sticky bottom bar. The offset
   * magnitude is intentionally larger than the spread so the shadow
   * clears the element edge and stays visible.
   */
  bottombar: {
    layers: [
      {
        color: `${SHADOW_BASE}${OPACITY[4]}`,
        offsetX: 0,
        offsetY: -8,
        blurRadius: 16,
        spreadRadius: -6,
      },
      {
        color: `${SHADOW_BASE}${OPACITY[6]}`,
        offsetX: 0,
        offsetY: -6,
        blurRadius: 8,
        spreadRadius: -6,
      },
    ],
  },
  /** Modal/dialog shadow */
  modal: {
    layers: [
      {
        color: `${SHADOW_BASE}${OPACITY[12]}`,
        offsetX: 0,
        offsetY: 12,
        blurRadius: 42,
        spreadRadius: -4,
      },
      {
        color: `${SHADOW_BASE}${OPACITY[12]}`,
        offsetX: 0,
        offsetY: 8,
        blurRadius: 18,
        spreadRadius: -6,
      },
    ],
  },
  /** Floating selection surfaces: Select list, Dropdown menu. Figma's values equal `modal` */
  dropdown: {
    layers: [
      {
        color: `${SHADOW_BASE}${OPACITY[12]}`,
        offsetX: 0,
        offsetY: 12,
        blurRadius: 42,
        spreadRadius: -4,
      },
      {
        color: `${SHADOW_BASE}${OPACITY[12]}`,
        offsetX: 0,
        offsetY: 8,
        blurRadius: 18,
        spreadRadius: -6,
      },
    ],
  },
  /** FAB at rest, hovered and loading: ✨Luce/shadow/fab. Black, as Figma draws it */
  fab: {
    layers: [
      {
        color: "#0000004D", // 30%
        offsetX: 0,
        offsetY: 1,
        blurRadius: 3,
        spreadRadius: 0,
      },
      {
        color: "#00000026", // 15%
        offsetX: 0,
        offsetY: 4,
        blurRadius: 8,
        spreadRadius: 3,
      },
    ],
  },
  /** FAB while pressed: lower and tighter than its idle shadow. Black, not grey-900 */
  "fab-pressed": {
    layers: [
      {
        color: "#00000033", // 20%
        offsetX: 0,
        offsetY: 1,
        blurRadius: 1.3,
        spreadRadius: 0,
      },
      {
        color: "#0000000D", // 5%
        offsetX: 0,
        offsetY: 1,
        blurRadius: 8,
        spreadRadius: 3,
      },
    ],
  },
  /** The Switch knob: ✨Luce/shadow/knob, a tight lift. Black, as Figma draws it */
  knob: {
    layers: [
      {
        color: "#0000001A", // 10%
        offsetX: 0,
        offsetY: 1,
        blurRadius: 3,
        spreadRadius: 0,
      },
      {
        color: "#0000001A", // 10%
        offsetX: 0,
        offsetY: 1,
        blurRadius: 2,
        spreadRadius: -1,
      },
    ],
  },
} as const;

export type ShadowKey = keyof typeof shadows;
